import React, { useEffect, useState } from "react";
import AdminSidebar from "../../components/AdminSidebar";
import "./admin.css";
import "./AdminTheatre.css";

const START_TIME_OPTIONS = [
  "10:00", "10:30", "11:00", "11:30", "12:00", "12:30",
  "13:00", "13:30", "14:00", "14:30", "15:00", "15:30",
  "16:00", "16:30", "17:00", "17:30", "18:00", "18:30",
];

const SEAT_ROWS = [
  { row: "A", count: 8 },
  { row: "B", count: 8 },
  { row: "C", count: 7 },
];

function formatTime(t) {
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${m.toString().padStart(2, "0")} ${period}`;
}

function formatBookingTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function AdminTheatre() {
  const [shows, setShows] = useState([]);
  const [activeShow, setActiveShow] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [movieName, setMovieName] = useState("");
  const [showDate, setShowDate] = useState("");
  const [startTime, setStartTime] = useState("");

  const API = process.env.REACT_APP_API_URL;
  const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/theatre/shows`, { headers: authHeader });
      if (!res.ok) {
        setError("Failed to load shows");
        return;
      }
      setShows(await res.json());
    } catch (err) {
      setError("Server error while loading shows");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const publishShow = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!movieName || !showDate || !startTime) {
      setError("Movie name, date, and start time are all required");
      return;
    }

    try {
      const res = await fetch(`${API}/api/theatre/shows`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ movieName, showDate, startTime }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to publish show");

      setMovieName("");
      setShowDate("");
      setStartTime("");
      setSuccess("Show published — residents can now book seats");
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteShow = async (id) => {
    await fetch(`${API}/api/theatre/shows/${id}`, { method: "DELETE", headers: authHeader });
    load();
  };

  const openShowDetails = (show) => {
    setActiveShow(show);
  };

  const bookedSeats = activeShow ? activeShow.seats.filter((s) => s.status === "booked") : [];

  return (
    <div className="admin-layout">
      <AdminSidebar />

      <div className="admin-content">
        <h1>Mini Theatre</h1>
        <p className="admin-sub">Publish shows and view booking details</p>

        <div className="theatre-admin-info">
          ₹3,000 + 18% GST per slot booking · Booking hours: 10:00 AM – 10:00 PM · Each slot is 3.5 hours · 23 seats
        </div>

        {error && <p className="admin-error">{error}</p>}
        {success && <p className="notice-success">{success}</p>}

        {!activeShow && (
          <>
            <form onSubmit={publishShow} className="notice-form">
              <label className="notice-label">
                Movie Name
                <input className="notice-input" value={movieName} onChange={(e) => setMovieName(e.target.value)} />
              </label>

              <label className="notice-label">
                Show Date
                <input type="date" className="notice-input" value={showDate} onChange={(e) => setShowDate(e.target.value)} />
              </label>

              <label className="notice-label">
                Start Time (slot runs 3.5 hours from this time)
                <select className="notice-input" value={startTime} onChange={(e) => setStartTime(e.target.value)}>
                  <option value="">Select start time</option>
                  {START_TIME_OPTIONS.map((t) => (
                    <option key={t} value={t}>{formatTime(t)}</option>
                  ))}
                </select>
              </label>

              <button type="submit" className="approve-btn">Publish Show</button>
            </form>

            <h2 className="notice-list-heading">Published Shows</h2>

            <div className="theatre-admin-list">
              {shows.map((s) => {
                const bookedCount = s.seats.filter((seat) => seat.status === "booked").length;
                return (
                  <div className="theatre-admin-item" key={s.id}>
                    <div className="theatre-admin-item-header">
                      <div className="theatre-admin-item-info" onClick={() => openShowDetails(s)} style={{ cursor: "pointer", flex: 1 }}>
                        <strong>{s.movieName}</strong>
                        <div className="theatre-admin-item-meta">
                          {s.showDate} · {formatTime(s.startTime)} – {formatTime(s.endTime)} · {bookedCount}/23 booked
                        </div>
                      </div>
                      <button className="reject-btn" onClick={() => deleteShow(s.id)}>Remove</button>
                    </div>
                  </div>
                );
              })}

              {shows.length === 0 && !error && (
                <p className="admin-empty">No shows published yet</p>
              )}
            </div>
          </>
        )}

        {activeShow && (
          <div className="theatre-admin-details">
            <button className="theatre-back-btn" onClick={() => setActiveShow(null)}>← Back to shows</button>

            <h2 className="theatre-show-title">{activeShow.movieName}</h2>
            <p className="theatre-show-subtitle">
              {activeShow.showDate} · {formatTime(activeShow.startTime)} – {formatTime(activeShow.endTime)}
            </p>

            <div className="theatre-details-tabs">
              <div className="theatre-tab-content">
                <h3 className="theatre-tab-heading">Seat Map</h3>
                <div className="theatre-screen">SCREEN</div>

                <div className="theatre-seat-map">
                  {SEAT_ROWS.map(({ row }) => (
                    <div className="theatre-row" key={row}>
                      <span className="theatre-row-label">{row}</span>
                      <div className="theatre-row-seats">
                        {activeShow.seats
                          .filter((s) => s.seatId.startsWith(row))
                          .map((seat) => (
                            <div
                              key={seat.seatId}
                              className="theatre-admin-seat"
                              title={
                                seat.status === "booked"
                                  ? `${seat.seatId}\n${seat.bookedBy.residentName} (${seat.bookedBy.unitNumber})`
                                  : seat.seatId
                              }
                            >
                              <span className="seat-id">{seat.seatId.slice(1)}</span>
                              {seat.status === "booked" && <span className="seat-resident">{seat.bookedBy.residentName.split(" ")[0]}</span>}
                            </div>
                          ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="theatre-legend-admin">
                  <span><i className="theatre-seat available"></i> Available</span>
                  <span><i className="theatre-seat booked"></i> Booked</span>
                </div>
              </div>

              <div className="theatre-tab-content">
                <h3 className="theatre-tab-heading">Booking Details ({bookedSeats.length})</h3>

                {bookedSeats.length > 0 ? (
                  <div className="theatre-booking-table">
                    <div className="theatre-booking-header">
                      <span>Seat</span>
                      <span>Resident</span>
                      <span>Unit</span>
                      <span>Tower</span>
                      <span>Booked At</span>
                    </div>

                    {bookedSeats.map((seat) => (
                      <div className="theatre-booking-row" key={seat.seatId}>
                        <span className="seat-badge">{seat.seatId}</span>
                        <span>{seat.bookedBy.residentName}</span>
                        <span>{seat.bookedBy.unitNumber || "—"}</span>
                        <span>{seat.bookedBy.tower || "—"}</span>
                        <span className="booking-time">{formatBookingTime(seat.bookedBy.bookedAt)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="admin-empty">No bookings yet</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}